/**
 * ReportCard Component - Phase 5 Implementation
 * Reusable report card component with preview chart and metrics
 * Following existing component patterns and design system
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { BarChart3, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ReportMetadata } from '../../types/reports';

export interface ReportCardProps {
  report: ReportMetadata;
  isLoading?: boolean;
  onClick: () => void;
  className?: string;
}

const ReportCard: React.FC<ReportCardProps> = ({ 
  report, 
  isLoading = false, 
  onClick,
  className 
}) => {
  const IconComponent = report.icon;
  const isPositiveTrend = (report.trend || 0) > 0;
  
  // Safety check for icon component
  const isValidIcon = IconComponent && typeof IconComponent === 'function';

  if (isLoading) {
    return (
      <Card className={cn("h-64 hover:shadow-md transition-shadow", className)}>
        <CardHeader className="space-y-2">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-4 w-full" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={cn(
        "h-64 hover:shadow-md transition-all duration-200 cursor-pointer hover:scale-[1.02]",
        className
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={cn("p-2 rounded-lg text-white", report.color)}>
              {isValidIcon ? (
                <IconComponent className="h-4 w-4" />
              ) : (
                <BarChart3 className="h-4 w-4" />
              )}
            </div>
            <div>
              <CardTitle className="text-sm font-medium">{report.title}</CardTitle>
              <Badge variant="outline" className="text-xs mt-1">
                {report.category}
              </Badge>
            </div>
          </div>
        </div>
        <CardDescription className="text-xs leading-relaxed">
          {report.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* No data available message */}
        <div className="h-16 bg-muted/30 rounded-lg flex items-center justify-center">
          <div className="flex items-center space-x-1 text-muted-foreground text-xs">
            <BarChart3 className="h-4 w-4" />
            <span>No preview data</span>
          </div>
        </div>
        
        {/* Report Metrics */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-muted-foreground">No data</span>
            {report.trend !== undefined && (
              <div className={cn(
                "flex items-center space-x-1 text-xs px-2 py-1 rounded-full",
                isPositiveTrend 
                  ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" 
                  : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
              )}>
                {isPositiveTrend ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                <span>{Math.abs(report.trend)}%</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Updated {report.lastUpdated || 'recently'}</span>
            <Button size="sm" variant="ghost" className="h-6 text-xs">
              View Details →
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportCard;